import { formatCubeValue, isNumericCubeFormat } from './cube_format.utils';
import { aggregateSeriesFromResultSets } from './pagination_pivot.utils';
import { getColumnStyle, getConditionalStyleValue } from './style_helpers.utils';
/**
 * Number-chart Neutral text color. The Neutral *palette* (and the Neutral option
 * inside Categorical) use dark grey so values stay readable on white cards.
 * Other chart types still use light Neutral (#F3F4F6) for fills.
 */
const NUMBER_CHART_NEUTRAL_COLOR = '#3A3B3D';
/**
 * Default color for every number value in a palette.
 * Unlike bar/line charts, number charts do NOT cycle colors across values —
 * all values share one default; CF rules may override individual measures.
 */
const getNumberChartPaletteDefault = (palette) => {
    switch (palette) {
        case 'categorical':
            // Neutral option within the categorical palette (number-chart readable form)
            return NUMBER_CHART_NEUTRAL_COLOR;
        case 'purple':
            return 'purple1';
        case 'greyscale':
            // Grey 1 — first swatch in the greyscale palette
            return 'neutral';
        case 'neutral':
        default:
            return NUMBER_CHART_NEUTRAL_COLOR;
    }
};
/** Map the Neutral token to dark grey for number-chart text when appropriate. */
const resolveNeutralToken = (color, palette) => {
    if (color !== 'neutral') {
        return color;
    }
    // Greyscale Grey 1 is intentionally the light Neutral fill token.
    if (palette === 'greyscale') {
        return color;
    }
    return NUMBER_CHART_NEUTRAL_COLOR;
};
const getNumberSize = (datasetCount) => {
    if (datasetCount <= 1) {
        return '9xl';
    }
    if (datasetCount === 2) {
        return '7xl';
    }
    return '6xl';
};
const isColumnStyleArray = (style) => Array.isArray(style);
/**
 * Read a color that is EXPLICITLY configured on the chart — a matching condition
 * or an explicit `style.color`. Returns the raw design-system token with no palette
 * default and no Neutral→dark-grey remap, i.e. exactly the pre-SSP-4192 behavior.
 *
 * Used for legacy report viewers (no `rid`) and legacy charts (no `numberValues`) so
 * conditional formatting that was already saved on those charts keeps rendering.
 */
const resolveExplicitColor = (numberValue, seriesKey, style) => {
    if (isColumnStyleArray(style)) {
        const columnStyle = getColumnStyle(seriesKey, style);
        if (columnStyle?.conditions || columnStyle?.condition) {
            return getConditionalStyleValue(numberValue, columnStyle) || undefined;
        }
        return undefined;
    }
    if (style?.conditions) {
        return getConditionalStyleValue(numberValue, style) || style?.color || undefined;
    }
    return style?.color;
};
/**
 * Resolve a number value's text color.
 *
 * Default = the palette's default color (same for every value).
 * Conditional formatting may override a single measure when its rule matches.
 *
 * The SSP-4192 palette default color only applies on new-flow reports (opened with a
 * `rid`) and in the builder preview. Legacy report viewers (no `rid`) and legacy charts
 * (no `numberValues`) never get the palette default — but they DO keep honoring colors
 * and conditions explicitly saved on the chart, so existing legacy report conditional
 * formatting still renders (this is the same `rid` gate used by SSP-4129).
 */
const resolveNumberChartColor = ({ numberValue, seriesKey, style, palette, isLegacyChart, applyValueFormatting, }) => {
    if (!applyValueFormatting || isLegacyChart) {
        return resolveExplicitColor(numberValue, seriesKey, style);
    }
    let computedColor = getNumberChartPaletteDefault(palette);
    if (isColumnStyleArray(style)) {
        const columnStyle = getColumnStyle(seriesKey, style);
        if (columnStyle?.conditions || columnStyle?.condition) {
            const conditionalColor = getConditionalStyleValue(numberValue, columnStyle);
            if (conditionalColor) {
                computedColor = resolveNeutralToken(conditionalColor, palette);
            }
        }
        return computedColor;
    }
    if (style?.color) {
        computedColor = resolveNeutralToken(style.color, palette);
    }
    if (style?.conditions) {
        const conditionalColor = getConditionalStyleValue(numberValue, style);
        if (conditionalColor) {
            computedColor = resolveNeutralToken(conditionalColor, palette);
        }
    }
    return computedColor;
};
export const buildNumberChartDatasets = ({ resultSets, pivotConfig, style, palette, numberValues, showLabels, applyValueFormatting = true, }) => {
    if (!resultSets.length) {
        return [];
    }
    const firstResultSet = resultSets[0];
    const aggregatedSeries = aggregateSeriesFromResultSets(resultSets, pivotConfig);
    const valueConfigByKey = new Map((numberValues || []).map((config) => [config.value.key, config]));
    const numberSize = getNumberSize(aggregatedSeries.length);
    const isLegacyChart = !numberValues || numberValues.length === 0;
    return aggregatedSeries.map((series) => {
        const numberValue = series.series[0]?.value ?? '0';
        const configuredValue = valueConfigByKey.get(series.key);
        const computedColor = resolveNumberChartColor({
            numberValue,
            seriesKey: series.key,
            style,
            palette,
            isLegacyChart,
            applyValueFormatting,
        });
        const measureAnnotation = firstResultSet.annotation().measures[series.key];
        const cubeFormat = measureAnnotation?.format;
        const hasCubeFormat = applyValueFormatting && isNumericCubeFormat(cubeFormat);
        const suffix = measureAnnotation?.meta?.suffix;
        // A Cube numeric format already renders units (%, K/M), so don't append a separate unit.
        const unitValue = hasCubeFormat ? '' : configuredValue?.unit || suffix || '';
        const displayNumber = hasCubeFormat ? formatCubeValue(numberValue, cubeFormat) : numberValue;
        let label;
        let title;
        if (isLegacyChart) {
            label = series.shortTitle || series.title;
        }
        else {
            // Number charts expose a single "Show labels" toggle in the builder. The configured
            // Label field renders below the value (label slot). showLegend is pie/scatter only.
            label = showLabels ? configuredValue?.label : undefined;
        }
        const legacyNumberStyle = !isColumnStyleArray(style) ? style : undefined;
        return {
            number: displayNumber,
            title,
            label,
            unit: unitValue
                ? {
                    size: legacyNumberStyle?.unit?.size || 'base',
                    value: unitValue,
                }
                : undefined,
            numberSize: isLegacyChart ? legacyNumberStyle?.numberSize || numberSize : numberSize,
            numberAlign: 'center',
            color: computedColor,
        };
    });
};
